import { Label } from '@eigtech/ui/components/label'
import { Switch } from '@eigtech/ui/components/switch'
import { FieldValues, useController } from 'react-hook-form'
import { WithFieldNameAndDefaultValueProps } from '../types/FieldProps'
import { BaseProps, FieldControl } from './FieldControl'

type Props<TFieldValues extends FieldValues = FieldValues> = {
  switchProps?: React.ComponentProps<typeof Switch>
  leftLabel?: React.ReactNode
  leftLabelProps?: React.ComponentProps<typeof Label>
  rightLabel?: React.ReactNode
  rightLabelProps?: React.ComponentProps<typeof Label>
} & BaseProps<TFieldValues>

export type SwitchFieldProps<TFieldValues extends FieldValues = FieldValues> =
  WithFieldNameAndDefaultValueProps<Props<TFieldValues>, TFieldValues>

export function SwitchField<TFieldValues extends FieldValues = FieldValues>({
  name,
  control,
  defaultValue,
  label,
  switchProps,
  leftLabel,
  leftLabelProps,
  rightLabel,
  rightLabelProps,
  ...props
}: SwitchFieldProps<TFieldValues>) {
  const {
    field,
    formState: { isSubmitting },
  } = useController({
    name,
    control,
    defaultValue,
  })

  return (
    <FieldControl control={control} disabled={isSubmitting} label={label} name={name} {...props}>
      <div className="flex flex-row items-center gap-2">
        {leftLabel && <Label {...leftLabelProps}>{leftLabel}</Label>}
        <Switch
          {...field}
          checked={field.value}
          onCheckedChange={field.onChange}
          {...switchProps}
        />
        {rightLabel && <Label {...rightLabelProps}>{rightLabel}</Label>}
      </div>
    </FieldControl>
  )
}

export type SwitchFieldComponent<TFieldValues extends FieldValues = FieldValues> =
  typeof SwitchField<TFieldValues>
